import { Box, Text } from '@chakra-ui/react'
import { FC, memo, useMemo, useState } from 'react'
import Select, { OptionsType } from 'react-select'
import slugify from 'slugify'

import { FilterProps } from '@base/@types/components/Shared/Filter'

// Busca sem diferenciar maiúsculas/acentos, como o filtro padrão do react-select
const DIACRITICS = new RegExp(
  `[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`,
  'g'
)
const normalizeText = (text: string) =>
  text.normalize('NFD').replace(DIACRITICS, '').toLowerCase()

interface IOptionsSelectedProps {
  label: string
  value: string | number
}

const Filter: FC<FilterProps> = ({
  label = '',
  whiteLabel = false,
  marginTop = 0,
  options = [],
  value = [],
  isMulti = false,
  isLoading = false,
  isDisabled = false,
  isSearchable = false,
  isRequired = false,
  isFollowUp = false,
  limit,
  menuPortalTarget,
  styles,
  onMenuOpen,
  onMenuClose,
  onChange = null
}) => {
  const [query, setQuery] = useState('')

  // Normaliza label/value uma vez por lista (não a cada tecla)
  const normalizedOptions = useMemo(
    () =>
      isSearchable
        ? options.map(item => ({
            item,
            label: normalizeText(String(item.label ?? '')),
            value: normalizeText(String(item.value ?? ''))
          }))
        : [],
    [options, isSearchable]
  )

  // Listas grandes: filtra a lista inteira pelo texto digitado e só depois limita a 15
  const defaultOptions = useMemo(() => {
    if (isSearchable) {
      const search = normalizeText(query.trim())
      if (search.length > 0) {
        // Pelo value: termo numérico só casa com o código exato; texto casa pelo início
        const isNumericSearch = /^\d+$/.test(search)
        return normalizedOptions
          .filter(
            option =>
              option.label.includes(search) ||
              (isNumericSearch
                ? option.value === search
                : option.value.startsWith(search))
          )
          .slice(0, 15)
          .map(option => option.item)
      }
      return options.slice(0, 15)
    }

    return options
  }, [options, normalizedOptions, isSearchable, query])

  const handleOnChange = (
    values: IOptionsSelectedProps | OptionsType<IOptionsSelectedProps>
  ) => {
    onChange &&
      onChange(
        limit && Array.isArray(values) ? values!.slice(0, limit) : values
      )
  }

  const handleFilterSelect = (inputValue: string) => {
    // Mantém a busca em sincronia com o que foi digitado (e limpa ao selecionar/fechar)
    setQuery(inputValue)

    const inputValueFormatted = Number(inputValue.replace(/ /g, ''))
    const ValidateValue = !!inputValueFormatted
    const separateValues = String(inputValue).split('')
    const existValues = separateValues.some(value => value === ' ')

    if (inputValue !== '' && existValues && ValidateValue) {
      setQuery(String(inputValue))

      let newOptions = [...value]

      const values = String(inputValue).split(' ')

      values.forEach(value => {
        const option = options.find(item => String(item.value) === value)

        if (option) {
          const existOption = newOptions.filter(
            item => item.value && String(item.value) === String(option.value)
          )

          if (existOption.length === 0) {
            newOptions = [...newOptions, option]
          }
        }
      })

      newOptions.length > 0 && handleOnChange(newOptions)

      document
        .querySelectorAll('.react-select__input')
        .forEach((item: any) => (item.style.visibility = 'hidden'))
    }
  }

  const handleBlur = () => {
    document
      .querySelectorAll('.react-select__input')
      .forEach((item: any) => (item.style.visibility = 'visible'))
  }
  return (
    <Box w="100%" marginTop={marginTop && marginTop}>
      <Text
        mb={label !== '' ? 2 : -1}
        fontSize="sm"
        color={whiteLabel && '#fff'}
      >
        {label}
      </Text>

      <Select
        id="react-select"
        inputId={`input-${slugify(label).toLowerCase()}`}
        instanceId={`${slugify(label).toLowerCase()}-value`}
        name={slugify(label.toLowerCase())}
        placeholder={isLoading ? 'Carregando...' : 'Selecione...'}
        classNamePrefix={'react-select'}
        className={isFollowUp && 'react-select__follow-up'}
        isMulti={isMulti}
        value={value}
        noOptionsMessage={() => 'Não há mais opções'}
        menuPlacement="auto"
        maxHeight={200}
        isDisabled={isLoading || isDisabled}
        isLoading={isLoading}
        isRequired={isRequired}
        menuPortalTarget={menuPortalTarget}
        styles={styles}
        // closeMenuOnSelect
        options={defaultOptions}
        onChange={values => handleOnChange(values)}
        onInputChange={event => handleFilterSelect(event)}
        onBlur={() => handleBlur()}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        css={{
          '.css-g1d714-ValueContainer': {
            maxHeight: '60px',
            overflow: 'auto'
          },
          'react-select__control': {
            background: 'red',
            'z-index': 999
          }
        }}
      />
    </Box>
  )
}

export default memo(Filter)
